import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcode, faBell, faBoxesStacked, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { eventBus } from '../bus/eventBus';
import CreateStockModal from '../components/Modal/CreateStockModal';
import ViewStockModal from '../components/Modal/ViewStockModal';
import Modal from '../components/Modal/Modal';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';

export function useProductActions({ onRefresh } = {}) {
    const { t } = useTranslation();
    const { settings } = useAuth();
    const [selectedStock, setSelectedStock] = useState(null);
    const [showStockModal, setShowStockModal] = useState(false);
    const [selectedStockProductId, setSelectedStockProductId] = useState(null);
    const [selectedStockProductName, setSelectedStockProductName] = useState(null);
    const [showViewStockModal, setShowViewStockModal] = useState(false);
    const [quickEdit, setQuickEdit] = useState(null);
    const [addingToCartId, setAddingToCartId] = useState(null);

    // `totalStock` is optional — callers that already have the row/product object pass it
    // through so we can warn before even hitting the API, instead of letting the user build
    // out a whole cart around an item that will only fail once they try to finalize the sale
    // (the backend still rejects it at that point too — this is just earlier, friendlier
    // feedback, not a replacement for that check).
    const addToCart = async (productId, totalStock) => {
        if ((totalStock ?? 0) <= 0 && settings?.sales_inventory_allow_out_of_stock_sales !== true) {
            toast.warning(t('product.out_of_stock_sale_disabled'));
            return;
        }
        setAddingToCartId(productId);
        try {
            const { data } = await axios.get(
                `${API_BASE_URL}/api/v1/cart/product/${productId}`,
                { withCredentials: true }
            );
            eventBus.emit('newCartItem', data);
        } catch {
            toast.error(t('product.error_add_to_cart'));
        } finally {
            setAddingToCartId(null);
        }
    };

    const handleAddStock = (productId) => {
        setSelectedStock({ orderableId: productId });
        setShowStockModal(true);
    };

    const handleViewStock = (productId, productName) => {
        setSelectedStockProductId(productId);
        setSelectedStockProductName(productName ?? null);
        setShowViewStockModal(true);
    };

    // prices (PPV / PPH) always shown with 2 decimals: 372 → "372.00"
    const isPriceField = (field) => field === 'ppv' || field === 'pph';
    const formatPrice = (value) => {
        const n = parseFloat(value);
        return value === '' || value == null || isNaN(n) ? (value ?? '') : n.toFixed(2);
    };

    const openQuickEdit = (productId, field, label, currentValue) => {
        setQuickEdit({ productId, field, label, value: isPriceField(field) ? formatPrice(currentValue) : (currentValue ?? '') });
    };

    const handleQuickEditSave = async () => {
        if (!quickEdit) return;
        const { productId, field, value } = quickEdit;
        const body = {};
        if (field === 'barcode') body.barcode = value;
        if (field === 'ppv') body.publicPrice = parseFloat(value);
        if (field === 'pph') body.hospitalPrice = parseFloat(value);
        if (field === 'minimumStock') body.minimumStock = parseInt(value, 10);
        if (field === 'maximumStock') body.maximumStock = parseInt(value, 10);
        try {
            await axios.patch(`${API_BASE_URL}/api/v1/products/${productId}`, body, { withCredentials: true });
            if (field === 'minimumStock') toast.success(t('product.stock_alert_updated', { count: parseInt(value, 10) }));
            if (field === 'maximumStock') toast.success(t('product.stock_max_updated', { count: parseInt(value, 10) }));
            setQuickEdit(null);
            onRefresh?.();
        } catch {
            toast.error(t('product.error_update'));
        }
    };

    // Explicitly clears the product's own threshold (sets it back to null) rather than saving
    // 0, which the backend would store as this product's own fixed value. For minimumStock that
    // means falling back to the pharmacy-wide default; maximumStock has no such default (see
    // Product#maximumStock) so clearing it just turns the overstock alert off for this product.
    const handleResetThreshold = async () => {
        if (!quickEdit) return;
        const { productId, field } = quickEdit;
        const body = field === 'maximumStock' ? { resetMaximumStock: true } : { resetMinimumStock: true };
        try {
            await axios.patch(`${API_BASE_URL}/api/v1/products/${productId}`, body, { withCredentials: true });
            toast.success(field === 'maximumStock' ? t('product.stock_max_reset') : t('product.stock_alert_reset'));
            setQuickEdit(null);
            onRefresh?.();
        } catch {
            toast.error(t('product.error_update'));
        }
    };

    const modals = (
        <>
            <CreateStockModal
                stock={selectedStock}
                show={showStockModal}
                onClose={() => setShowStockModal(false)}
                onSave={() => { setShowStockModal(false); onRefresh?.(); }}
            />

            <ViewStockModal
                productId={selectedStockProductId}
                productName={selectedStockProductName}
                show={showViewStockModal}
                onClose={() => setShowViewStockModal(false)}
                onBatchUpdate={() => onRefresh?.()}
            />

            <Modal
                show={!!quickEdit}
                title={quickEdit?.label}
                onClose={() => setQuickEdit(null)}
                classSize="modal-sm"
                footer={
                    <>
                        <button className="btn btn-light btn-sm" onClick={() => setQuickEdit(null)}>{t('Cancel')}</button>
                        <button className="btn btn-primary btn-sm fw-bold" onClick={handleQuickEditSave}>{t('Save')}</button>
                    </>
                }
            >
                {quickEdit && (
                    <>
                        {(quickEdit.field === 'minimumStock' || quickEdit.field === 'maximumStock') && (
                            <div style={{
                                display: 'flex', gap: 10, alignItems: 'flex-start',
                                background: '#f0f9ff', border: '1px solid #bae6fd',
                                borderRadius: 8, padding: '10px 12px', marginBottom: 12,
                                fontSize: 12, color: '#0369a1', lineHeight: 1.5,
                            }}>
                                <FontAwesomeIcon icon={quickEdit.field === 'maximumStock' ? faBoxesStacked : faBell} style={{ marginTop: 2, flexShrink: 0 }} />
                                <span>{quickEdit.field === 'maximumStock' ? t('product.stock_max_helper') : t('product.stock_alert_helper')}</span>
                            </div>
                        )}
                        <div style={{ position: 'relative' }}>
                            <input
                                autoFocus
                                type={quickEdit.field === 'ppv' || quickEdit.field === 'pph' ? 'number' : 'text'}
                                inputMode={quickEdit.field === 'minimumStock' || quickEdit.field === 'maximumStock' ? 'numeric' : undefined}
                                step={quickEdit.field === 'ppv' || quickEdit.field === 'pph' ? '0.01' : undefined}
                                className="form-control"
                                style={['minimumStock', 'maximumStock', 'barcode'].includes(quickEdit.field) ? { paddingRight: 34 } : undefined}
                                value={quickEdit.value}
                                onChange={e => setQuickEdit(q => ({ ...q, value: e.target.value }))}
                                onBlur={() => { if (isPriceField(quickEdit.field)) setQuickEdit(q => (q ? { ...q, value: formatPrice(q.value) } : q)); }}
                                onKeyDown={e => { if (e.key === 'Enter') handleQuickEditSave(); if (e.key === 'Escape') setQuickEdit(null); }}
                            />
                            {(quickEdit.field === 'minimumStock' || quickEdit.field === 'maximumStock') && (
                                <button
                                    type="button"
                                    onClick={handleResetThreshold}
                                    title={quickEdit.field === 'maximumStock' ? t('product.stock_max_reset_link') : t('product.stock_alert_reset_link')}
                                    style={{
                                        position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)',
                                        width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.color = '#1d4ed8'; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; }}
                                >
                                    <FontAwesomeIcon icon={faRotateLeft} style={{ fontSize: 13 }} />
                                </button>
                            )}
                            {/* barcode field: the scanner icon inside, on the right */}
                            {quickEdit.field === 'barcode' && (
                                <FontAwesomeIcon
                                    icon={faBarcode}
                                    aria-hidden="true"
                                    style={{
                                        position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)',
                                        fontSize: 15, color: '#9ca3af', pointerEvents: 'none',
                                    }}
                                />
                            )}
                        </div>
                        {/* helper text under the field — it has focus, so a scanner types the code straight in */}
                        {quickEdit.field === 'barcode' && (
                            <div className="form-text" style={{ fontSize: 12, color: '#9ca3af', marginTop: 6, lineHeight: 1.45 }}>
                                {t('product.barcode_helper')}
                            </div>
                        )}
                    </>
                )}
            </Modal>
        </>
    );

    return {
        addToCart,
        addingToCartId,
        handleAddStock,
        handleViewStock,
        openQuickEdit,
        modals,
    };
}
